import { useQuery } from '@tanstack/react-query';
import { CircleAlert, LogOut, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { pipelineApi } from '@/lib/api';
import { useAuth, useCurrentUser } from '@/lib/auth/AuthProvider';
import type { PipelineState } from '@/types/api';
import { cn } from '@/lib/cn';

const POLL_INTERVAL_MS = 30_000;

/**
 * Spec S03 §"Studio header" — pipeline status always visible + error queue badge.
 */
export function StudioHeader() {
  const { t } = useTranslation('studio');
  const { data } = useQuery({
    queryKey: ['studio', 'pipeline-status'],
    queryFn: () => pipelineApi.status(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-slate-200 bg-white px-4">
      <PipelineStatusIndicator
        state={data?.pipeline_status ?? 'idle'}
        processed={data?.documents_processed ?? 0}
        total={data?.documents_total ?? 0}
        triplets={data?.triplets_staged ?? 0}
      />

      <div className="flex-1" />

      {(data?.error_queue_total ?? 0) > 0 && (
        <Button asChild variant="ghost" size="sm">
          <Link to="/studio/errors" className="flex items-center gap-1">
            <CircleAlert className="h-3.5 w-3.5 text-red-500" aria-hidden />
            <Badge variant="error">
              {t('errorBadge', { count: data?.error_queue_total ?? 0 })}
            </Badge>
          </Link>
        </Button>
      )}

      <Button asChild variant="secondary" size="sm">
        <Link to="/app/chat">{t('switchToApp')}</Link>
      </Button>

      <LanguageSwitcher />

      <UserMenu />
    </header>
  );
}

// ─── Pipeline status indicator ──────────────────────────────────

function PipelineStatusIndicator({
  state,
  processed,
  total,
  triplets,
}: {
  state: PipelineState;
  processed: number;
  total: number;
  triplets: number;
}) {
  const { t } = useTranslation('studio');
  const color = STATE_COLOR[state];
  const message = t(`pipelineStatus.${state}`, { processed, total, triplets });
  return (
    <div className="flex items-center gap-2 text-small text-slate-700">
      <span
        aria-hidden
        className={cn('h-2.5 w-2.5 rounded-full', color)}
      />
      <span>{message}</span>
    </div>
  );
}

const STATE_COLOR: Record<PipelineState, string> = {
  idle: 'bg-slate-400',
  running: 'bg-blue-500 animate-pulse',
  checkpoint: 'bg-amber-500',
  committing: 'bg-blue-600 animate-pulse',
  committed: 'bg-green-500',
  error: 'bg-red-500',
};


// ─── User menu ──────────────────────────────────────────────────

function UserMenu() {
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const user = useCurrentUser();
  if (!user) return null;
  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('userMenu.profile')}>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 text-h4 text-brand-900">
            {initials}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/portal/home" className="flex items-center gap-2">
            <UserIcon className="h-3.5 w-3.5" aria-hidden /> {t('userMenu.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()} className="text-red-700">
          <LogOut className="mr-2 h-3.5 w-3.5" aria-hidden />
          {t('userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
